// src/components/Home.js - Unified Dashboard with Recording + Stats
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config.js";

function Home() {
  const [athletes, setAthletes] = useState([]);
  const [loadingAthletes, setLoadingAthletes] = useState(true);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");

  const loggedInUser = JSON.parse(localStorage.getItem("user") || "null");
  const token = loggedInUser?.token;
  const userRole = loggedInUser?.role;

  // Load athletes and summary stats
  useEffect(() => {
    if (!token) {
      setAthletes([]);
      setLoadingAthletes(false);
      return;
    }

    const fetchData = async () => {
      setLoadingAthletes(true);
      try {
        const [athletesRes, summaryRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/athletes`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          userRole !== 'athlete' ? axios.get(`${API_BASE_URL}/api/stats/coach-summary`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch((error) => {
            console.error('[Home] Error fetching summary:', error.response?.data || error.message);
            return { data: { summary: null } };
          }) : Promise.resolve({ data: { summary: null } })
        ]);
        
        setAthletes(athletesRes.data || []);
        console.log('[Home] Summary data received:', summaryRes.data);
        console.log('[Home] Summary object:', summaryRes.data?.summary);
        console.log('[Home] User role:', userRole);
        setSummary(summaryRes.data?.summary || null);
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError("Error loading data.");
      } finally {
        setLoadingAthletes(false);
      }
    };

    fetchData();
  }, [token, userRole]);

  if (loadingAthletes) {
    return <div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>;
  }

  return (
    <div style={{ padding: "1rem", maxWidth: "1400px", margin: "0 auto", paddingBottom: "80px" }}>
      {/* Header */}
      <div style={{ marginBottom: "2rem" }}>
        <h1 style={{ fontSize: "2rem", fontWeight: "700", marginBottom: "0.5rem" }}>
          {userRole === 'athlete' ? '👋 Welcome' : '🎥 Record & Track'}
        </h1>
        <p style={{ color: "#6b7280", fontSize: "1rem" }}>
          {userRole === 'athlete' 
            ? 'View your training progress and performance stats'
            : 'Record videos, classify landings, and monitor team progress'}
        </p>
      </div>

      {error && <p style={{ color: "red", marginBottom: "1rem" }}>{error}</p>}

      {/* Quick Stats Cards - Only for coaches/admins */}
      {userRole !== 'athlete' && summary && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "1rem",
          marginBottom: "2rem"
        }}>
          <StatCard 
            icon="👥" 
            label="Athletes on Roster" 
            value={summary.total_athletes || 0}
            color="#3b82f6"
          />
          <StatCard 
            icon="🎥" 
            label="Floor Videos" 
            value={summary.event_counts?.Floor || 0}
            color="#f59e0b"
          />
          <StatCard 
            icon="🎥" 
            label="Beam Videos" 
            value={summary.event_counts?.Beam || 0}
            color="#10b981"
          />
          <StatCard 
            icon="🎥" 
            label="Vault Videos" 
            value={summary.event_counts?.Vault || 0}
            color="#8b5cf6"
          />
          <StatCard 
            icon="🎥" 
            label="Bars Videos" 
            value={summary.event_counts?.Bars || 0}
            color="#ef4444"
          />
          <StatCard 
            icon="📊" 
            label="Total Videos" 
            value={(summary.event_counts?.Floor || 0) + (summary.event_counts?.Beam || 0) + (summary.event_counts?.Vault || 0) + (summary.event_counts?.Bars || 0)}
            color="#06b6d4"
          />
        </div>
      )}
      
      {/* Debug: Show if summary is missing */}
      {userRole !== 'athlete' && !summary && (
        <div style={{ padding: "1rem", background: "#fef3c7", borderRadius: "8px", marginBottom: "1rem" }}>
          ⚠️ Summary stats not loaded. Check console for errors.
        </div>
      )}

      {/* Quick Action Button - View Full Stats */}
      <div style={{ marginBottom: "2rem" }}>
        <Link to="/stats">
          <button
            style={{
              background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
              color: "#fff",
              padding: "0.75rem 1.5rem",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "1rem",
              boxShadow: "0 4px 6px rgba(59, 130, 246, 0.3)",
              transition: "all 0.2s"
            }}
            onMouseOver={(e) => e.target.style.transform = "translateY(-2px)"}
            onMouseOut={(e) => e.target.style.transform = "translateY(0)"}
          >
            📊 View Detailed Stats →
          </button>
        </Link>
      </div>

      {/* Video Recording Section - Only for coaches/admins */}
      {userRole !== 'athlete' && (
        <div style={{
          background: "#f9fafb",
          padding: "2rem",
          borderRadius: "12px",
          border: "1px solid #e5e7eb"
        }}>
          {!loadingAthletes && athletes.length === 0 && (
            <div style={{
              background: "#fff3cd",
              padding: "1rem 1.5rem",
              borderRadius: "8px",
              border: "1px solid #ffc107",
              marginBottom: "1.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "0.75rem"
            }}>
              <span style={{ color: "#856404", fontWeight: "500" }}>
                ⚠️ No athletes on roster yet — add athletes to assign recordings
              </span>
              <Link to="/create-athlete">
                <button style={{
                  background: "#ffc107", color: "#000", padding: "0.5rem 1rem",
                  border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", fontSize: "0.875rem"
                }}>
                  + Add Athlete
                </button>
              </Link>
            </div>
          )}
          <Link to="/video" style={{ textDecoration: "none" }}>
            <button style={{
              background: "#2563eb", color: "#fff", padding: "0.85rem 1.5rem",
              border: "none", borderRadius: "8px", cursor: "pointer",
              fontWeight: "600", fontSize: "1rem", display: "inline-flex",
              alignItems: "center", gap: "0.5rem"
            }}>
              🎥 Record & Analyze Video →
            </button>
          </Link>
          <p style={{ color: "#6b7280", fontSize: "0.875rem", marginTop: "0.75rem", marginBottom: 0 }}>
            Capture a rotation, run pose analysis, and review clips on the Video page.
          </p>
        </div>
      )}

      {/* Athlete View - Link to their stats */}
      {userRole === 'athlete' && (
        <div style={{
          background: "#f0f9ff",
          padding: "2rem",
          borderRadius: "12px",
          border: "1px solid #bfdbfe",
          textAlign: "center"
        }}>
          <h2 style={{ fontSize: "1.5rem", marginBottom: "1rem" }}>📊 Your Training Stats</h2>
          <p style={{ marginBottom: "1.5rem", color: "#6b7280" }}>
            View your progress, landing statistics, and performance trends
          </p>
          <Link to="/stats">
            <button
              style={{
                background: "#3b82f6",
                color: "white",
                padding: "1rem 2rem",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "600",
                fontSize: "1.125rem"
              }}
            >
              View My Stats →
            </button>
          </Link>
        </div>
      )}

    </div>
  );
}

// Stat Card Component
function StatCard({ icon, label, value, color }) {
  return (
    <div style={{
      background: "white",
      padding: "1.25rem",
      borderRadius: "10px",
      border: "1px solid #e5e7eb",
      textAlign: "center",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
      transition: "transform 0.2s"
    }}
    onMouseOver={(e) => e.currentTarget.style.transform = "translateY(-4px)"}
    onMouseOut={(e) => e.currentTarget.style.transform = "translateY(0)"}
    >
      <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>{icon}</div>
      <div style={{ fontSize: "0.875rem", color: "#6b7280", marginBottom: "0.5rem" }}>
        {label}
      </div>
      <div style={{ fontSize: "1.75rem", fontWeight: "700", color }}>
        {value}
      </div>
    </div>
  );
}

export default Home;
